import { Injectable, InjectionToken } from '@angular/core';
import { AuthenticationData, ImagingOpenParams, PacsStudyData as PacsStudyDataNg1 } from 'estensa-module';
import { DeepPascalCase } from '../../utils/types';
import { noop } from 'lodash-es';
import moment from 'moment';

export const RADIOLOGY_API_CLIENT_IMAGING_SERVICE = new InjectionToken('radiology.api.client.imaging.service');

export interface PacsStudyDataVnext {
    patient: {
        patientId?: string;
        fullName?: string;
        sex?: string;
        birthDate?: string;
    };
    study: {
        studyInstanceUID?: string;
        studyDate?: string;
        accessionNumber?: string;
        modality?: string;
    };
}

export type ExaminationStatusForImaging =
    | 'Unknown' // Error status
    | 'Open' // Normal examination state
    | 'Close' // Approve/Sign examination
    | 'Closed' // Opened an approved/signed examination
    | 'ReOpened' // Undo sign/approve
    | 'Lock'; // Unused - cannot open locked examination at the moment

export enum ScreeningUserRole {
    None = 0,
    Reader = 1,
    Reviewer = 2,
    OnlyView = 3,
}

export interface RequestModelViewStudies {
    studies: { Tags: Record<string, string> }[];
    viewForConsultation: boolean;
    discardOption: { Ask: boolean; Action: 'Discard' | 'Append' };
    iSSource: 'Unknown' | 'Radio' | 'Cardio';
    screeningMGOption: { enable: boolean; role: ScreeningUserRole; userID: string; roleLabel: number };
}

export const DEFAULT_OPEN_IMAGES_OPTIONS: Omit<RequestModelViewStudies, 'studies'> = {
    discardOption: {
        Ask: false,
        Action: 'Discard',
    },
    iSSource: 'Cardio',
    screeningMGOption: {
        enable: false,
        role: ScreeningUserRole.None,
        roleLabel: 0,
        userID: '',
    },
    viewForConsultation: false,
};

export interface PacsTuple {
    patientBirthDate: string;
    patientID: string;
    patientName: string;
    patientSex: string;
    studyAccessionNumber: string;
    studyDate: string;
    studyID: string;
    studyInstanceUID: string;
}

export interface KeyImageMessage {
    instanceUID: string;
    isDeletable: boolean;
    nPla: PacsTuple;
    repositoryKey: string;
    requestDeleteAll: boolean;
    requestRefreshIcon: boolean;
}

export interface KeyImageInfoMessage {
    studyUID: string;
    seriesUID: string;
    instanceUID: string;
    isKeyImage: boolean;
    isDeletable: boolean;
    isReadOnly: boolean;
    repositoryKey: string;
    nPla: PacsTuple;
}

export interface KeyImage {
    SOPInstanceUID: string;
    RepositoryKey: string;
    IdExamination: string;
    Caption: string;
    ExcludedFromReport: boolean;
    IsDeletable: boolean;
}

export interface ModelUSStressEchoAssociateImages {
    stages: string[];
    views: string[];
}

export interface ModelShowImage {
    StructuredReportSOPInstanceUID?: string;
    ReferenceSOPInstanceUID: string;
    Frame?: number;
    GraphicCoord?: string;
    PresentationStateSOPInstaceUID?: string;
}

export interface ModelUSMeasure {
    MeasureGuid: string;
    MeasureIsCalculated: boolean;
    MeasureLabel: string;
}

export function keyImageFromMessage(msg: DeepPascalCase<KeyImageMessage>): KeyImage {
    return {
        SOPInstanceUID: msg.InstanceUID,
        RepositoryKey: msg.RepositoryKey,
        IdExamination: '',
        Caption: '',
        ExcludedFromReport: false,
        IsDeletable: msg.IsDeletable,
    };
}

export interface USMeasureMessage {
    examGuid: string;
    nPla: PacsTuple;
    xMLReport: string;
    xMLCustomMeasure: string;
}

export interface USMeasureReport {
    id: string;
    bsa: number;
    isMeanEnable: boolean;
    applicationKind: number;
}

export interface BaseSRMessage {
    repositoryKey: string;
    instanceUID: string;
    nPla: PacsTuple;
}

export interface USStressEchoHeartRateItem {
    stage: string;
    view: string;
    heartRate: number;
    acquisition: string;
}

export interface USStressEchoProtocolItem {
    stage: string;
    view: string;
    acquisition: string;
    value: string;
    scheme: string;
    meaning: string;
}

export interface USStressEchoInitMessage {
    nPla: PacsTuple;
    stages: string[]; // Nomi stage - da mappare in lsUsStressStage
    views: string[]; // Nomi viste - da mappare in lsUsStressView
    studyUID: string; // Da controllare se serve
    heartRate: {
        items: USStressEchoHeartRateItem[]; // Da controllare cosa farcene
    };
    protocol: {
        items: USStressEchoProtocolItem[]; // Da controllare cosa farcene
    };
}

export interface USStressEchoItem {
    stage?: string;
    view?: string;
}

export interface USStressEchoSelectMessage {
    nPla: PacsTuple;
    rows: number; // @Mirko viene gestita?
    columns: number;
    content: USStressEchoItem[];
}

export enum XmlRequestType {
    DiaImagingRVStrain,
    DiaImagingLVStrain,
    DiaImagingLVWallMotion,
    CaasQardia,
    TeraRecon,
}

export function isVnextPacsStudyData(v: PacsStudyDataNg1 | PacsStudyDataVnext[]): v is PacsStudyDataVnext[] {
    return v instanceof Array;
}

export function toNg1PacsStudyData(data: PacsStudyDataVnext): PacsStudyDataNg1 {
    const result = new PacsStudyDataNg1();
    result.accessionNumber = data.study.accessionNumber;
    result.dateTime = data.study.studyDate as any;
    result.modality = data.study.modality;
    result.patientBirthDate = data.patient.birthDate as any;
    result.patientID = data.patient.patientId;
    result.patientName = data.patient.fullName;
    result.patientSex = data.patient.sex;
    result.studyInstanceUID = data.study.studyInstanceUID;

    return result;
}

export function fromNg1PacsStudyData(data: PacsStudyDataNg1): PacsStudyDataVnext {
    return {
        patient: {
            birthDate: data.patientBirthDate ? moment(data.patientBirthDate).format('YYYYMMDD') : undefined,
            patientId: data.patientID,
            fullName: data.patientName,
            sex: data.patientSex,
        },
        study: {
            accessionNumber: data.accessionNumber,
            studyDate: data.dateTime ? moment(data.dateTime).format('YYYYMMDD') : undefined,
            modality: data.modality,
            studyInstanceUID: data.studyInstanceUID,
        },
    };
}

export interface IImagingServiceNg1 {
    areImagesOpen: boolean;
    discardImages(askConfirmation?: boolean): Promise<void>;
    openImages(
        imagingData: PacsStudyDataNg1,
        credentials: AuthenticationData | null,
        options: Partial<ImagingOpenParams>,
    ): Promise<void>;
    setViewerHelper(helper: IViewerHelper): void;
}
export interface IViewerHelper {
    readonly isVNext: boolean;
    readonly viewerType: string;

    discardImages(askConfirmation?: boolean): Promise<void>;
    init(): Promise<void>;
    openImages(imagingData: PacsStudyDataVnext[]): Promise<void>;
    openViewer(): Promise<void>;
    setExaminationStatus?(idExaminations: string[], status: ExaminationStatusForImaging): Promise<void>;
    deleteKeyImage?(data: KeyImageMessage): Promise<void>;
    usStressEchoSelectStage?(stage: string): Promise<void>;
    usStressEchoSelectView?(view: string): Promise<void>;
    uSStressEchoAssociateImages?(model: ModelUSStressEchoAssociateImages): Promise<void>;
    showImage?(model: ModelShowImage): Promise<void>;
    uSMeasureShow?(model: ModelUSMeasure): Promise<void>;
    // Compatibilità html5
    openImagesRequired(imagingData: PacsStudyDataNg1[]): void;
    discardImagesRequired(): void;
    closeViewerRequired(): void;
    isFormSubmitting(): boolean;
}

@Injectable()
export class NullImagingService implements IImagingServiceNg1 {
    areImagesOpen = false;
    discardImages = async () => {
        /* noop */
    };
    openImages = async () => {
        /* noop */
    };
    setViewerHelper = noop;
}
